/**
 * ----------------------------------------------------------------------------------------------------------------------
 * CURRENCY SELECTOR
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Shopify sets the currency from the country, so each currency option of the selector submits a country. The Liquid
 * snippet (see snippets/localization-selector.liquid) already picks a sensible fallback country (the shop country, then
 * the country matching the currency code, then the first country using that currency).
 *
 * This element refines that choice with the visitor's detected country: when the detected country uses the currency of
 * an option, the option submits that country instead (e.g. a visitor from Germany selecting EUR goes to Germany).
 */

let detectedCountryPromise;

const getDetectedCountry = () => {
  if (detectedCountryPromise) {
    return detectedCountryPromise;
  }

  detectedCountryPromise = (async () => {
    try {
      const cachedCountry = window.sessionStorage.getItem('currency-selector:detected-country');

      if (cachedCountry) {
        return cachedCountry;
      }
    } catch {
      // Storage can be unavailable (private mode, blocked cookies): simply fetch the country again
    }

    try {
      const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true');
      const suggestions = await response.json();
      const countryCode = suggestions?.detected_values?.country?.handle;

      if (countryCode) {
        try {
          window.sessionStorage.setItem('currency-selector:detected-country', countryCode);
        } catch {
          // Storage can be unavailable, the country is still returned
        }
      }

      return countryCode;
    } catch {
      return null;
    }
  })();

  return detectedCountryPromise;
};

if (!window.customElements.get('currency-selector')) {
  window.customElements.define(
    'currency-selector',
    class CurrencySelector extends HTMLElement {
      async connectedCallback() {
        const countryCurrencies = JSON.parse(this.querySelector('[data-country-currencies]')?.textContent || '{}');
        const detectedCountry = await getDetectedCountry();
        const detectedCurrency = countryCurrencies[detectedCountry];

        if (!detectedCurrency) {
          return; // The visitor's country is not part of the markets, keep the fallback countries
        }

        // The option of the active currency keeps the active country, so re-selecting it never changes the country
        this.querySelectorAll(`button[data-currency="${detectedCurrency}"]:not([aria-selected="true"])`).forEach((button) => {
          button.value = detectedCountry;
        });
      }
    }
  );
}
